import { formatarCNPJ, validarCNPJ } from '@spark/utils/formatters';

export const DIAS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
export const DIAS_CURTOS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
export const MAX_TURNOS = 3;
export const MAX_NOME = 120;

const MINUTOS_DIA = 1440;
const TURNO_PADRAO = { horaInicio: '07:00', horaFim: '14:00' };

export const TEXTO_MODO = { PROXIMO_TURNO: 'Próximo turno', CHEGADA: 'Desde a chegada' };

const pad = n => String(n).padStart(2, '0');

// 'HH:mm' | 'HH:mm:ss' -> minutos desde 00:00, ou null quando inválido
export const paraMinutos = hora => {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(hora ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
};

export const deMinutos = minutos => `${pad(Math.floor(minutos / 60))}:${pad(minutos % 60)}`;

export const apenasDigitos = (valor, limite) => {
  const digitos = String(valor ?? '').replace(/\D/g, '');
  return limite ? digitos.slice(0, limite) : digitos;
};

export const mascararRaiz = valor => {
  const d = apenasDigitos(valor, 8);
  return [d.slice(0, 2), d.slice(2, 5), d.slice(5, 8)].filter(Boolean).join('.');
};

const novoDia = aberto => ({ aberto, turnos: [{ ...TURNO_PADRAO }] });

export const rascunhoVazio = () => ({
  nome: '',
  tipo: 'RAIZ',
  cnpjRaiz: '',
  cnpj: '',
  modoForaJanela: 'PROXIMO_TURNO',
  dias: DIAS.map((_, i) => novoDia(i < 5)),
});

// Turnos parseáveis (em minutos) ordenados por início; independe de estarem válidos
const turnosOrdenados = dia =>
  (dia?.turnos ?? [])
    .map(t => ({ inicio: paraMinutos(t.horaInicio), fim: paraMinutos(t.horaFim) }))
    .filter(t => t.inicio !== null && t.fim !== null)
    .sort((a, b) => a.inicio - b.inicio);

// Classifica o dia aberto: { erro: 'limite'|'invalido'|'sobreposto'|null, turnos }
const analisarDia = dia => {
  const brutos = dia?.turnos ?? [];
  if (brutos.length > MAX_TURNOS) return { erro: 'limite', turnos: [] };

  const turnos = brutos.map(t => ({ inicio: paraMinutos(t.horaInicio), fim: paraMinutos(t.horaFim) }));
  if (turnos.some(t => t.inicio === null || t.fim === null || t.fim <= t.inicio)) {
    return { erro: 'invalido', turnos: [] };
  }

  turnos.sort((a, b) => a.inicio - b.inicio);
  for (let i = 1; i < turnos.length; i += 1) {
    if (turnos[i].inicio < turnos[i - 1].fim) return { erro: 'sobreposto', turnos: [] };
  }
  return { erro: null, turnos };
};

export const turnosValidos = dia => {
  if (!dia?.aberto) return null;
  const { erro, turnos } = analisarDia(dia);
  return erro || turnos.length === 0 ? null : turnos;
};

export const paraRascunho = janela => {
  const dias = DIAS.map((_, i) => {
    const turnos = (janela?.turnos ?? [])
      .filter(t => t.diaSemana === i + 1)
      .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
      .map(t => ({
        horaInicio: String(t.horaInicio ?? '').slice(0, 5),
        horaFim: String(t.horaFim ?? '').slice(0, 5),
      }));
    return turnos.length ? { aberto: true, turnos } : novoDia(false);
  });

  return {
    nome: janela?.nome ?? '',
    tipo: janela?.tipo ?? 'RAIZ',
    cnpjRaiz: apenasDigitos(janela?.cnpjRaiz, 8),
    cnpj: apenasDigitos(janela?.cnpj, 14),
    modoForaJanela: janela?.modoForaJanela ?? 'PROXIMO_TURNO',
    dias,
  };
};

export const paraPayload = rascunho => {
  const porCnpj = rascunho.tipo === 'CNPJ';
  const turnos = rascunho.dias.flatMap((dia, i) =>
    dia.aberto
      ? turnosOrdenados(dia).map((t, idx) => ({
          diaSemana: i + 1,
          ordem: idx + 1,
          horaInicio: `${deMinutos(t.inicio)}:00`,
          horaFim: `${deMinutos(t.fim)}:00`,
        }))
      : []
  );

  return {
    nome: String(rascunho.nome ?? '').trim(),
    tipo: rascunho.tipo,
    cnpjRaiz: porCnpj ? apenasDigitos(rascunho.cnpj, 8) : apenasDigitos(rascunho.cnpjRaiz, 8),
    cnpj: porCnpj ? apenasDigitos(rascunho.cnpj, 14) : null,
    modoForaJanela: rascunho.modoForaJanela,
    turnos,
  };
};

export const textoDia = dia => {
  const turnos = dia?.aberto ? turnosOrdenados(dia) : [];
  if (!turnos.length) return 'Fechado';
  return turnos.map(t => `${deMinutos(t.inicio)}–${deMinutos(t.fim)}`).join(' · ');
};

export const resumoTurnos = dias => {
  const grupos = [];
  dias.forEach((dia, i) => {
    const texto = textoDia(dia);
    if (texto === 'Fechado') return;
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.texto === texto && ultimo.fim === i - 1) {
      ultimo.fim = i;
    } else {
      grupos.push({ texto, inicio: i, fim: i });
    }
  });

  if (!grupos.length) return 'Sem recebimento';
  return grupos
    .map(g => {
      const nome =
        g.inicio === g.fim ? DIAS_CURTOS[g.inicio] : `${DIAS_CURTOS[g.inicio]}–${DIAS_CURTOS[g.fim]}`;
      return `${nome} ${g.texto}`;
    })
    .join(' · ');
};

export const textoDestino = rascunho =>
  rascunho.tipo === 'CNPJ'
    ? `CNPJ ${formatarCNPJ(apenasDigitos(rascunho.cnpj, 14))}`
    : `Raiz ${mascararRaiz(rascunho.cnpjRaiz)}`;

export const diferencas = (original, atual) => {
  const campos = [
    ['Nome', r => String(r.nome ?? '').trim()],
    ['Aplica-se a', textoDestino],
    ...DIAS.map((nome, i) => [nome, r => textoDia(r.dias[i])]),
    ['Chegada fora da janela', r => TEXTO_MODO[r.modoForaJanela] ?? r.modoForaJanela],
  ];

  return campos
    .map(([campo, ler]) => ({ campo, anterior: ler(original), novo: ler(atual) }))
    .filter(d => d.anterior !== d.novo);
};

export const validarJanela = rascunho => {
  const erros = [];

  const nome = String(rascunho.nome ?? '').trim();
  if (!nome) erros.push('Informe o nome da janela.');
  else if (nome.length > MAX_NOME) erros.push(`O nome da janela deve ter no máximo ${MAX_NOME} caracteres.`);

  if (rascunho.tipo === 'CNPJ') {
    if (!validarCNPJ(apenasDigitos(rascunho.cnpj))) erros.push('CNPJ inválido.');
  } else if (apenasDigitos(rascunho.cnpjRaiz).length !== 8) {
    erros.push('A raiz do CNPJ deve ter 8 dígitos.');
  }

  const mensagens = {
    limite: `no máximo ${MAX_TURNOS} turnos.`,
    invalido: 'o fim do turno deve ser depois do início.',
    sobreposto: 'turnos sobrepostos.',
  };
  let algumAberto = false;
  rascunho.dias.forEach((dia, i) => {
    if (!dia.aberto || !(dia.turnos ?? []).length) return;
    algumAberto = true;
    const { erro } = analisarDia(dia);
    if (erro) erros.push(`${DIAS[i]}: ${mensagens[erro]}`);
  });

  if (!algumAberto) erros.push('Ative ao menos um dia de recebimento.');

  return erros;
};

const turnoDaChegada = (turnos, t) => turnos.find(turno => turno.fim > t);

export const calcularInicioContagem = (rascunho, diaIndice, hora) => {
  const t = paraMinutos(hora);
  if (t === null) return null;

  if (rascunho.modoForaJanela === 'CHEGADA') {
    const turnos = turnosValidos(rascunho.dias[diaIndice]) ?? [];
    const dentro = turnos.some(turno => turno.inicio <= t && t < turno.fim);
    return { diaIndice, minutos: t, esperaForaMinutos: 0, dentroDoTurno: dentro, semanaSeguinte: false };
  }

  for (let k = 0; k <= 7; k += 1) {
    const indice = (diaIndice + k) % 7;
    const turnos = turnosValidos(rascunho.dias[indice]);
    if (!turnos) continue;

    const turno = k === 0 ? turnoDaChegada(turnos, t) : turnos[0];
    if (!turno) continue;

    const inicio = k === 0 ? Math.max(t, turno.inicio) : turno.inicio;
    return {
      diaIndice: indice,
      minutos: inicio,
      esperaForaMinutos: k * MINUTOS_DIA + inicio - t,
      dentroDoTurno: k === 0 && turno.inicio <= t,
      semanaSeguinte: k === 7,
    };
  }

  return null;
};
